import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ActionBoardService, OutstandingSummary} from './action-board.service';

/**
 * CR-20260922-001 — the overlay (§5.7): a transparent colour wash over the application content while
 * the logged-in user has at least one outstanding item, in the colour of the highest outstanding
 * severity. CR-20260924-001 F3 (owner 2026-09-24): the wash is for the user's OWN items only —
 * viewing a subordinate's board never changes it. It never blocks clicks (pointer-events: none). It is driven by the FULL outstanding set
 * — the board's scope filter never touches it. Mounted inside the host content area so the tab bar,
 * header and footer stay crisp.
 *
 * Owner 2026-09-22: no wash over the Action Board itself — only over other panels. The wash steps
 * aside while a board panel is the active tab in the layout tab on screen.
 *
 * CR-20260927-001: a SUPER USER may switch the wash off (gear on the board's header; a user setting,
 * default OFF for super users). The service answers `overlayEnabled`; non-super users are never gated.
 */
@Component({
    selector: 'ets-action-board-overlay',
    template: `<div class="action-board-wash" [class.red]="severity === 'red'" [class.orange]="severity === 'orange'"
                    [class.yellow]="severity === 'yellow'" [class.green]="severity === 'green'" [class.on]="!!severity && !boardOnScreen && overlayEnabled"></div>`,
    styles: [`
        :host {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
            z-index: 8000000;
        }

        .action-board-wash {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
            opacity: 0;
            transition: opacity 400ms ease, background-color 400ms ease;
            background-color: transparent;
        }

        .action-board-wash.on {
            opacity: 1;
        }

        .action-board-wash.red {
            background-color: rgba(220, 38, 38, 0.13);
            box-shadow: inset 0 0 0 3px rgba(220, 38, 38, 0.55);
        }

        .action-board-wash.orange {
            background-color: rgba(245, 158, 11, 0.11);
            box-shadow: inset 0 0 0 3px rgba(245, 158, 11, 0.55);
        }

        .action-board-wash.green {
            background-color: rgba(34, 197, 94, 0.09);
            box-shadow: inset 0 0 0 3px rgba(34, 197, 94, 0.5);
        }

        .action-board-wash.yellow {
            background-color: rgba(250, 204, 21, 0.09);
            box-shadow: inset 0 0 0 3px rgba(250, 204, 21, 0.5);
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionBoardOverlayComponent implements OnInit, OnChanges, OnDestroy {

    constructor(
        private readonly _service: ActionBoardService,
        private readonly _changeDetector: ChangeDetectorRef
    ) {
    }

    private readonly _unsubscriber = new Subject<void>();

    /** The layout tab on screen (from the host); the wash hides when a board is active in it. */
    @Input() activeLayoutTabId: string | null = null;

    severity: string = '';
    boardOnScreen = false;
    overlayEnabled = true;

    ngOnInit(): void {
        this._service.ownSummary$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe((s: OutstandingSummary | null) => {
                this.severity = s ? s.highest : '';
                this.refresh();
            });

        // CR-20260927-001: the super user's Overlay switch; re-evaluated on every change.
        this._service.preferences$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.refresh());

        this._service.boardVisibility$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.refresh());

        // The wash must be right from login (§5.7 "evaluated on login"): read the own board so the
        // summary exists before the panel is ever opened. (Moved here from the footer badge when the
        // badge was hidden, owner 2026-09-22.) This read also writes Delivered (CR-20260924-001 F1)
        // and retries with back-off (F10); it never throws.
        this._service.loadOwnBoardWithRetry();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.activeLayoutTabId) {
            this.refresh();
        }
    }

    ngOnDestroy(): void {
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    private refresh(): void {
        this.boardOnScreen = this._service.isBoardOnScreen(this.activeLayoutTabId);
        this.overlayEnabled = this._service.overlayEnabled;
        this._changeDetector.detectChanges();
    }
}
